import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { useMyEntries } from '../../hooks/useSweepstakes';
import type { ContestView } from '../../api/sweepstakes';

interface ContestBannerProps {
  contestView: ContestView;
  collectionSlug: string;
}

function useCountdown(endDate: string) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const now = Date.now();
      const end = new Date(endDate).getTime();
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft('Ended');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m`);
      }
    };

    update();
    const interval = setInterval(update, 60_000);
    return () => clearInterval(interval);
  }, [endDate]);

  return timeLeft;
}

export const ContestBanner: React.FC<ContestBannerProps> = ({ contestView, collectionSlug }) => {
  const { contest, prizeTiers, stats } = contestView;
  const isActive = contest.status === 'active';
  const isSnapshotTaken = contest.status === 'snapshot_pending';
  const isMintRewards = contest.contest_type === 'mint_rewards';

  const timeLeft = useCountdown(isSnapshotTaken ? contest.starts_at : contest.ends_at);
  const { entryCount, rank } = useMyEntries(isActive ? contest.id : undefined);

  if (!isActive && !isSnapshotTaken && contest.status !== 'scheduled') return null;

  const entryLabel = isMintRewards ? 'mint' : 'purchase';
  const entryLabelPlural = isMintRewards ? 'mints' : 'purchases';

  return (
    <div className="w-full border border-brand-600 rounded-md px-4 py-3 mb-4 bg-default-background contest-glow">
      <style>{`
        @keyframes contest-glow {
          0%, 100% { box-shadow: 0 0 6px rgba(0,200,200,0.2), inset 0 0 6px rgba(0,200,200,0.05); }
          50% { box-shadow: 0 0 20px rgba(0,200,200,0.5), inset 0 0 10px rgba(0,200,200,0.08); }
        }
        .contest-glow { animation: contest-glow 2s ease-in-out infinite; }
      `}</style>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant={isActive ? 'success' : 'warning'}>
              {isActive
                ? (isMintRewards ? 'LIVE MINT CONTEST' : 'LIVE CONTEST')
                : (isMintRewards ? 'UPCOMING MINT CONTEST' : 'UPCOMING CONTEST')
              }
            </Badge>
            <span className="font-medium text-default-font truncate">{contest.name}</span>
            <span className="text-sm text-neutral-400 shrink-0">
              {isSnapshotTaken ? 'Starts in ' : 'Ends in '}
              <span className="font-medium text-default-font">{timeLeft}</span>
            </span>
          </div>
          <div className="text-sm text-default-font mt-0.5">
            <span>Prizes:</span>
            <div className="flex flex-col ml-3">
              {prizeTiers.map((tier) => (
                <span key={tier.id}>
                  {'– '}
                  {tier.type === 'leaderboard'
                    ? `Most ${entryLabelPlural} (${tier.winner_count} winner${tier.winner_count > 1 ? 's' : ''}): "${tier.description}"`
                    : `Random raffle (1 ticket per ${entryLabel}, ${tier.winner_count} winner${tier.winner_count > 1 ? 's' : ''}): "${tier.description}"`
                  }
                </span>
              ))}
            </div>
            {isSnapshotTaken && !isMintRewards && (
              <span className="text-amber-400 mt-0.5">Eligible listings have been snapshotted — new listings will not qualify</span>
            )}
            {isMintRewards && isActive && (
              <span className="text-brand-500 mt-0.5">Each mint = 1 entry</span>
            )}
          </div>
          {isActive && (
            <div className="flex flex-col text-sm text-neutral-400 mt-0.5">
              <span>{stats.entries} total entries from {stats.participants} participants</span>
              {rank != null && (
                <span className="text-brand-600 font-medium">
                  Your rank: #{rank} ({entryCount} {entryCount === 1 ? 'entry' : 'entries'})
                </span>
              )}
            </div>
          )}
        </div>
        {collectionSlug && (
          <Link to={`/collections/${collectionSlug}/contest`} className="shrink-0 self-start sm:self-center">
            <Button variant="brand-primary">
              View Contest
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};
